/**
 * The ALFIE icon set: inline SVG on a 24px grid with a 1.5px stroke, rounded
 * caps and joins. Inline rather than an icon font or sprite so there is no
 * extra request, no FOUT, and every glyph inherits `currentColor` and the
 * surrounding font size.
 *
 * Adding one: keep the 24px viewBox and the shared `base()` attributes, draw
 * with strokes (not fills) so it stays legible at 13px, and give it a name
 * that says what it *means* in ALFIE, not what it looks like. `IconName` is
 * derived from the object, so a new entry is immediately usable anywhere an
 * icon name is accepted.
 */
import type { SVGProps } from 'react';

type IconProps = SVGProps<SVGSVGElement>;

function base(props: IconProps) {
  return {
    width: 18,
    height: 18,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.5,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    ...props,
  };
}

export const Icon = {
  Dashboard: (p: IconProps) => (
    <svg {...base(p)}><rect x="3" y="3" width="7" height="9" /><rect x="14" y="3" width="7" height="5" /><rect x="14" y="12" width="7" height="9" /><rect x="3" y="16" width="7" height="5" /></svg>
  ),
  Chat: (p: IconProps) => (
    <svg {...base(p)}><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
  ),
  Doc: (p: IconProps) => (
    <svg {...base(p)}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /><path d="M8 13h8M8 17h8" /></svg>
  ),
  Summarize: (p: IconProps) => (
    <svg {...base(p)}><path d="M4 6h16M4 12h10M4 18h7" /></svg>
  ),
  Facts: (p: IconProps) => (
    <svg {...base(p)}><path d="M4 4h10l6 6v10a0 0 0 0 1 0 0H4z" /><path d="M14 4v6h6" /><circle cx="9.5" cy="14.5" r="1.5" /></svg>
  ),
  Engine: (p: IconProps) => (
    <svg {...base(p)}><circle cx="12" cy="12" r="3" /><path d="M12 2v3M12 19v3M2 12h3M19 12h3M5 5l2 2M17 17l2 2M19 5l-2 2M7 17l-2 2" /></svg>
  ),
  Users: (p: IconProps) => (
    <svg {...base(p)}><circle cx="9" cy="8" r="3" /><path d="M3 20a6 6 0 0 1 12 0" /><path d="M16 6a3 3 0 0 1 0 6M21 20a6 6 0 0 0-5-5.9" /></svg>
  ),
  Audit: (p: IconProps) => (
    <svg {...base(p)}><path d="M12 2v6M12 8l4-2M12 8 8 6" /><circle cx="12" cy="14" r="8" /><path d="M12 14h4" /></svg>
  ),
  Library: (p: IconProps) => (
    <svg {...base(p)}><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" /></svg>
  ),
  Layers: (p: IconProps) => (
    <svg {...base(p)}><path d="m12 2 9 5-9 5-9-5 9-5z" /><path d="m3 12 9 5 9-5M3 17l9 5 9-5" /></svg>
  ),
  Content: (p: IconProps) => (
    <svg {...base(p)}><rect x="3" y="3" width="18" height="18" rx="1" /><path d="M3 9h18M9 9v12" /></svg>
  ),
  Ingest: (p: IconProps) => (
    <svg {...base(p)}><path d="M12 3v12M8 11l4 4 4-4" /><path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" /></svg>
  ),
  Plus: (p: IconProps) => <svg {...base(p)}><path d="M12 5v14M5 12h14" /></svg>,
  Send: (p: IconProps) => <svg {...base(p)}><path d="M22 2 11 13M22 2l-7 20-4-9-9-4 20-7z" /></svg>,
  Trash: (p: IconProps) => (
    <svg {...base(p)}><path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" /></svg>
  ),
  Refresh: (p: IconProps) => (
    <svg {...base(p)}><path d="M21 12a9 9 0 1 1-3-6.7L21 8" /><path d="M21 3v5h-5" /></svg>
  ),
  Download: (p: IconProps) => (
    <svg {...base(p)}><path d="M12 3v12M7 10l5 5 5-5" /><path d="M5 21h14" /></svg>
  ),
  Logout: (p: IconProps) => (
    <svg {...base(p)}><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><path d="M16 17l5-5-5-5M21 12H9" /></svg>
  ),
  Check: (p: IconProps) => <svg {...base(p)}><path d="M20 6 9 17l-5-5" /></svg>,
  Edit: (p: IconProps) => (
    <svg {...base(p)}><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg>
  ),
  History: (p: IconProps) => (
    <svg {...base(p)}><path d="M3 12a9 9 0 1 0 3-6.7" /><path d="M3 4v5h5" /><path d="M12 7v5l3 2" /></svg>
  ),
  Copy: (p: IconProps) => (
    <svg {...base(p)}><rect x="9" y="9" width="11" height="11" rx="1" /><path d="M5 15V5a1 1 0 0 1 1-1h10" /></svg>
  ),
  Branch: (p: IconProps) => (
    <svg {...base(p)}><path d="M6 3v12" /><circle cx="18" cy="6" r="3" /><circle cx="6" cy="18" r="3" /><path d="M18 9a9 9 0 0 1-9 9" /></svg>
  ),
  Menu: (p: IconProps) => <svg {...base(p)}><path d="M4 6h16M4 12h16M4 18h16" /></svg>,
  Bell: (p: IconProps) => (
    <svg {...base(p)}><path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.7 21a2 2 0 0 1-3.4 0" /></svg>
  ),
  Close: (p: IconProps) => <svg {...base(p)}><path d="M18 6 6 18M6 6l12 12" /></svg>,
  Pulse: (p: IconProps) => (
    <svg {...base(p)}><path d="M3 12h4l3 8 4-16 3 8h4" /></svg>
  ),
  Alert: (p: IconProps) => (
    <svg {...base(p)}><path d="M12 9v4M12 17h.01" /><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" /></svg>
  ),
  Info: (p: IconProps) => (
    <svg {...base(p)}><circle cx="12" cy="12" r="9" /><path d="M12 16v-5M12 8h.01" /></svg>
  ),
  Shield: (p: IconProps) => (
    <svg {...base(p)}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>
  ),
  Clock: (p: IconProps) => (
    <svg {...base(p)}><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
  ),
  Sun: (p: IconProps) => (
    <svg {...base(p)}><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" /></svg>
  ),
  Moon: (p: IconProps) => (
    <svg {...base(p)}><path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" /></svg>
  ),
  Monitor: (p: IconProps) => (
    <svg {...base(p)}><rect x="2" y="4" width="20" height="13" rx="2" /><path d="M8 21h8M12 17v4" /></svg>
  ),
  Command: (p: IconProps) => (
    <svg {...base(p)}><path d="M9 6a3 3 0 1 0-3 3h12a3 3 0 1 0-3-3v12a3 3 0 1 0 3-3H6a3 3 0 1 0 3 3z" /></svg>
  ),
  Chevron: (p: IconProps) => <svg {...base(p)}><path d="m9 18 6-6-6-6" /></svg>,
  ChevronDown: (p: IconProps) => <svg {...base(p)}><path d="m6 9 6 6 6-6" /></svg>,
  External: (p: IconProps) => (
    <svg {...base(p)}><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><path d="M15 3h6v6M10 14 21 3" /></svg>
  ),
  Filter: (p: IconProps) => <svg {...base(p)}><path d="M3 5h18l-7 8v6l-4 2v-8L3 5z" /></svg>,
  Offline: (p: IconProps) => (
    <svg {...base(p)}><path d="M2 2l20 20" /><path d="M5 12.5a10 10 0 0 1 4-2.4M1.5 8.8a15 15 0 0 1 5-3.2M22.5 8.8a15 15 0 0 0-9.6-3.4M19 12.5a10 10 0 0 0-3-2" /><path d="M8.5 16a5 5 0 0 1 7 0M12 20h.01" /></svg>
  ),
  Activity: (p: IconProps) => (
    <svg {...base(p)}><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3.5 2" /><path d="M12 3a9 9 0 0 1 9 9" strokeWidth={2.4} /></svg>
  ),
  Sparkle: (p: IconProps) => (
    <svg {...base(p)}><path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z" /><path d="M18 16l.8 2.2L21 19l-2.2.8L18 22l-.8-2.2L15 19l2.2-.8z" /></svg>
  ),
  Scale: (p: IconProps) => (
    <svg {...base(p)}><path d="M12 3v18M7 21h10" /><path d="M6 7h12M6 7 3 14h6zM18 7l3 7h-6z" /></svg>
  ),
  Save: (p: IconProps) => (
    <svg {...base(p)}><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" /><path d="M17 21v-8H7v8M7 3v5h8" /></svg>
  ),
  Upload: (p: IconProps) => (
    <svg {...base(p)}><path d="M12 17V5M8 9l4-4 4 4" /><path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" /></svg>
  ),
  Search: (p: IconProps) => (
    <svg {...base(p)}><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" /></svg>
  ),
};

export type IconName = keyof typeof Icon;
